import { ArrowClockwise, NewspaperClipping } from "@phosphor-icons/react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { AppPromo } from "./components/AppPromo";
import { Header } from "./components/Header";
import { NewsRanking } from "./components/NewsRanking";
import { NewsstandHero } from "./components/NewsstandHero";
import { fetchValidated, withRevision } from "./lib/data";
import { selectVisualStories } from "./lib/editorial";
import { comicIndexSchema, comicIssueSchema, newsFeedSchema, newsIndexSchema } from "./schemas";
import type { Article, Category, ComicEdition, ComicIssue, NewsFeed, NewsIndex } from "./types";

const ArticleDrawer = lazy(() => import("./components/ArticleDrawer").then(module => ({ default: module.ArticleDrawer })));
const EditionPicker = lazy(() => import("./components/EditionPicker").then(module => ({ default: module.EditionPicker })));
const KomixReader = lazy(() => import("./components/KomixReader").then(module => ({ default: module.KomixReader })));
const SearchDialog = lazy(() => import("./components/SearchDialog").then(module => ({ default: module.SearchDialog })));

type Theme = "light" | "dark";

const categoryNames: Record<Category, string> = {
  Dzisiaj: "Najnowsze wiadomości",
  AI: "Sztuczna inteligencja",
  Technologia: "Technologia",
  Polska: "Polska",
  Świat: "Świat",
};

function initialTheme(): Theme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

function articleIdFromHash() {
  const match = window.location.hash.match(/^#article=(.+)$/);
  if (!match) return "";
  try { return decodeURIComponent(match[1]); } catch { return ""; }
}

function comicIdFromHash() {
  const match = window.location.hash.match(/^#komix=(.+)$/);
  if (!match) return "";
  try { return decodeURIComponent(match[1]); } catch { return ""; }
}

export default function App() {
  const [active, setActive] = useState<Category>("Dzisiaj");
  const [activeEditionId, setActiveEditionId] = useState("latest");
  const [feedRevision, setFeedRevision] = useState(0);
  const [selected, setSelected] = useState<Article | null>(null);
  const [selectedComic, setSelectedComic] = useState<ComicEdition | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [editionPickerOpen, setEditionPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const indexGeneration = useRef("");
  const articleHistoryDepth = useRef(Number(window.history.state?.newsArticleDepth ?? 0));

  const { data: editionIndex } = useSWR<NewsIndex>(
    "/data/news-index.json",
    url => fetchValidated<NewsIndex>(url, newsIndexSchema),
    { refreshInterval: 30_000, dedupingInterval: 10_000, revalidateOnFocus: true },
  );
  const { data: comicIndex } = useSWR(
    "/data/komix-index.json",
    url => fetchValidated(url, comicIndexSchema),
    { refreshInterval: 30_000, dedupingInterval: 10_000, revalidateOnFocus: true },
  );

  const activeEdition = useMemo(
    () => editionIndex?.editions.find(edition => edition.id === activeEditionId),
    [activeEditionId, editionIndex],
  );
  const activeFeedUrl = activeEditionId === "latest" ? "/data/news-feed.json" : activeEdition?.url;
  const feedKey = activeFeedUrl ? withRevision(activeFeedUrl, feedRevision) : null;
  const { data: feed, error: feedError, isLoading: feedLoading, mutate: refreshFeed } = useSWR<NewsFeed>(
    feedKey,
    (url: string) => fetchValidated<NewsFeed>(url, newsFeedSchema),
    {
      refreshInterval: activeEditionId === "latest" ? 30_000 : 0,
      dedupingInterval: 8_000,
      keepPreviousData: true,
      revalidateOnFocus: true,
    },
  );

  const latestIssueUrl = comicIndex?.latest?.issueUrl;
  const { data: latestIssue = null } = useSWR<ComicIssue>(
    latestIssueUrl ?? null,
    (url: string) => fetchValidated<ComicIssue>(url, comicIssueSchema),
    { dedupingInterval: 60_000, revalidateOnFocus: false },
  );
  const selectedIssueUrl = selectedComic?.issueUrl;
  const {
    data: selectedIssue = null,
    error: selectedIssueError,
    isLoading: selectedIssueLoading,
  } = useSWR<ComicIssue>(
    selectedIssueUrl ?? null,
    (url: string) => fetchValidated<ComicIssue>(url, comicIssueSchema),
    { dedupingInterval: 60_000, revalidateOnFocus: false },
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("news-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!editionIndex?.generatedAt) return;
    if (indexGeneration.current && indexGeneration.current !== editionIndex.generatedAt) {
      if (activeEditionId === "latest") {
        setFeedRevision(value => value + 1);
        void refreshFeed();
      } else {
        setUpdateAvailable(true);
      }
    }
    indexGeneration.current = editionIndex.generatedAt;
  }, [activeEditionId, editionIndex?.generatedAt, refreshFeed]);

  useEffect(() => {
    if (!feed) return;
    const syncArticle = () => {
      articleHistoryDepth.current = Number(window.history.state?.newsArticleDepth ?? 0);
      const articleId = articleIdFromHash();
      setSelected(articleId ? feed.articles.find(article => article.id === articleId) ?? null : null);
    };
    syncArticle();
    window.addEventListener("popstate", syncArticle);
    return () => window.removeEventListener("popstate", syncArticle);
  }, [feed]);

  useEffect(() => {
    if (!comicIndex) return;
    const syncComic = () => {
      const comicId = comicIdFromHash();
      setSelectedComic(comicId ? comicIndex.editions.find(edition => edition.id === comicId) ?? null : null);
    };
    syncComic();
    window.addEventListener("popstate", syncComic);
    return () => window.removeEventListener("popstate", syncComic);
  }, [comicIndex]);

  const articles = useMemo(() => {
    if (!feed) return [];
    const normalized = query.trim().toLocaleLowerCase("pl");
    const filtered = feed.articles.filter(article => {
      const inCategory = active === "Dzisiaj" || article.category === active;
      const haystack = `${article.title} ${article.summary} ${article.tags.join(" ")}`.toLocaleLowerCase("pl");
      return inCategory && (!normalized || haystack.includes(normalized));
    });
    const featuredIndex = active === "Dzisiaj" && feed.featuredArticleId
      ? filtered.findIndex(article => article.id === feed.featuredArticleId)
      : -1;
    if (featuredIndex <= 0) return filtered;
    return [filtered[featuredIndex], ...filtered.slice(0, featuredIndex), ...filtered.slice(featuredIndex + 1)];
  }, [active, feed, query]);

  const { lead, starters } = useMemo(() => selectVisualStories(articles), [articles]);

  const openArticle = useCallback((article: Article) => {
    const nextDepth = articleHistoryDepth.current + 1;
    articleHistoryDepth.current = nextDepth;
    window.history.pushState({ newsArticle: article.id, newsArticleDepth: nextDepth }, "", `#article=${encodeURIComponent(article.id)}`);
    setSelected(article);
  }, []);

  const closeArticle = useCallback(() => {
    const depth = articleHistoryDepth.current;
    articleHistoryDepth.current = 0;
    if (depth > 0) window.history.go(-depth);
    else {
      window.history.replaceState(null, "", "#top");
      setSelected(null);
    }
  }, []);

  const openComic = useCallback((edition?: ComicEdition | null) => {
    if (!edition) return;
    window.history.pushState({ newsComic: edition.id }, "", `#komix=${encodeURIComponent(edition.id)}`);
    setSelectedComic(edition);
  }, []);

  const switchComicEdition = useCallback((edition: ComicEdition) => {
    window.history.replaceState({ newsComic: edition.id }, "", `#komix=${encodeURIComponent(edition.id)}`);
    setSelectedComic(edition);
  }, []);

  const closeComic = useCallback(() => {
    if (window.history.state?.newsComic) window.history.back();
    else {
      window.history.replaceState(null, "", "#top");
      setSelectedComic(null);
    }
  }, []);

  const chooseEdition = useCallback((id: string) => {
    setActiveEditionId(id);
    setActive("Dzisiaj");
    setQuery("");
    setSelected(null);
    setUpdateAvailable(false);
    window.history.replaceState(null, "", "#top");
  }, []);

  const relatedArticles = useMemo(() => {
    if (!feed || !selected) return [];
    const preferred = (selected.relatedIds ?? [])
      .map(id => feed.articles.find(article => article.id === id))
      .filter((article): article is Article => Boolean(article));
    const rest = feed.articles.filter(article => article.id !== selected.id && !preferred.some(item => item.id === article.id));
    return [...preferred, ...rest.filter(article => article.category === selected.category), ...rest].slice(0, 3);
  }, [feed, selected]);

  const displayDate = feed
    ? new Intl.DateTimeFormat("pl-PL", { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${feed.runDate}T12:00:00`))
    : "Ładowanie wydania";

  return <div id="top">
    <Header
      active={active}
      onCategory={category => { setActive(category); setQuery(""); }}
      onSearch={() => setSearchOpen(true)}
      onEditions={() => setEditionPickerOpen(true)}
      onComic={() => openComic(comicIndex?.latest)}
      comicAvailable={Boolean(comicIndex?.latest)}
      theme={theme}
      onToggleTheme={() => setTheme(value => value === "light" ? "dark" : "light")}
    />

    <main>
      <div className="edition-line">
        <span>{displayDate}</span>
        <button type="button" onClick={() => setEditionPickerOpen(true)}>
          {activeEditionId === "latest" ? "Najnowszy briefing" : activeEdition?.label ?? "Archiwum"} · aktualizacja {feed?.runTime ?? "—"}
        </button>
        <span className={feedLoading ? "is-refreshing" : ""}><ArrowClockwise weight="bold" /> odświeżanie co 30 s</span>
      </div>

      {!feed && feedLoading && <div className="loading-state"><NewspaperClipping weight="duotone" /><strong>Składamy najnowsze wydanie…</strong></div>}
      {feedError && <div className="error-state"><strong>Nie udało się otworzyć wydania.</strong><button type="button" onClick={() => void refreshFeed()}>Spróbuj ponownie</button></div>}
      {feed && articles.length === 0 && <div className="error-state"><strong>Brak pasujących wiadomości.</strong><button type="button" onClick={() => { setActive("Dzisiaj"); setQuery(""); }}>Wyczyść filtry</button></div>}

      {lead && <NewsstandHero
        lead={lead}
        starters={starters}
        comic={comicIndex?.latest}
        issue={latestIssue}
        onOpenArticle={openArticle}
        onOpenComic={() => openComic(comicIndex?.latest)}
      />}

      {feed && articles.length > 0 && <NewsRanking
        articles={articles}
        onOpen={openArticle}
        title={categoryNames[active]}
      />}

      {feed && <AppPromo />}
      <footer>
        <div className="brand brand--footer"><span><b>NEWS.</b><strong>PAWEŁTAŃSKI</strong></span></div>
        <p>Niezależny, ilustrowany przegląd informacji. Fakty, kontekst i źródła w jednym miejscu.</p>
        <small>Wiadomości w kadrach · {feed?.generatedAt ? new Date(feed.generatedAt).toLocaleString("pl-PL") : ""}</small>
      </footer>
    </main>

    {updateAvailable && <button className="update-notice" type="button" onClick={() => chooseEdition("latest")}>
      Nowe wydanie jest dostępne <ArrowClockwise weight="bold" />
    </button>}

    <Suspense fallback={null}>
      {searchOpen && <SearchDialog open query={query} resultCount={articles.length} onOpenChange={setSearchOpen} onQuery={setQuery} />}
      {editionPickerOpen && <EditionPicker index={editionIndex ?? null} activeId={activeEditionId} onSelect={chooseEdition} onClose={() => setEditionPickerOpen(false)} />}
      <ArticleDrawer article={selected} relatedArticles={relatedArticles} onSelect={openArticle} onClose={closeArticle} />
      <KomixReader
        edition={selectedComic}
        editions={comicIndex?.editions ?? []}
        issue={selectedIssue}
        loading={selectedIssueLoading}
        error={selectedIssueError ? "Wersja ekranowa chwilowo nie odpowiada. Wydanie PDF pozostaje dostępne." : ""}
        onEdition={switchComicEdition}
        onClose={closeComic}
      />
    </Suspense>
  </div>;
}
